import React, { useState } from 'react';

import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Feedbacks from 'screens/StrengthDetail/Feedbacks';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import {
  Divider1,
  StyledBox,
  StyledButton,
  StyledGrid,
  StyledIconButton,
  StyledTypography,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import NoFeedback from 'assets/assetsV1/illustrations/newIllustrations/noFeedback.svg';
import CurvedArrow from 'assets/images/curved-arrow.svg?react';

import { FEEDBACK_FORM_TYPE, SENTIMENTS_TYPES } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';
import { FeedbackIcon } from 'constants/icons';

import { getFeedbackV1 } from 'services/developments';

import { Typography13 } from 'uiToolkit/Typography';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

const PraiseAdviceSection = ({ employeeId, tagId, accessible, competencyId }) => {
  const fetchPraise = (limit, offset) => getFeedbackV1({ employeeId, limit, offset, feedbackType: 'KUDOS', tagId });

  const fetchFeedback = (limit, offset) =>
    getFeedbackV1({ employeeId, limit, offset, feedbackType: 'FEEDBACK', tagId });

  const [isPraiseEmpty, setIsPraiseEmpty] = useState(false);
  const [isAdviceEmpty, setIsAdviceEmpty] = useState(false);

  const history = useHistory();

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const showShareFeedbackButton = loggedInEmployeeId !== employeeId;

  const handleRequestNewFeedbackClick = () => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.REQUESTED,
      newForm: true,
      empId: employeeId ?? null,
      competencyId,
      source: 'Develop Side Panel',
    });
  };

  const handleShareNewFeedbackClick = async () => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.PROVIDED,
      newForm: true,
      empId: employeeId ?? null,
      feedbackType: SENTIMENTS_TYPES.appreciative.value,
      competencyId,
      source: 'Develop Side Panel',
    });
  };

  if (isPraiseEmpty && isAdviceEmpty) {
    // combined view if both advice and praise are empty
    return (
      <>
        <StyledBox bottomGutter={2}>
          <VerticallyAlignedFlexContainer style={{ position: 'relative' }}>
            <StyledTypography weight={600} variant="body1">
              Praise/Feedback
            </StyledTypography>

            {Boolean(accessible && !showShareFeedbackButton) && (
              <StyledButton style={{ marginLeft: 'auto' }} onClick={handleRequestNewFeedbackClick}>
                <VerticallyAlignedFlexContainer>
                  <StyledIconButton color="primary" borderColor="primary" size="tinier" padding="xLarge">
                    <FeedbackIcon size="fixed20px" color="primary" />
                  </StyledIconButton>
                  <Typography13 leftGutter={1} color="primary">
                    Request Praise/Feedback
                  </Typography13>
                </VerticallyAlignedFlexContainer>
              </StyledButton>
            )}
            {accessible && !showShareFeedbackButton ? (
              <ArrowIconContainer>
                <CurvedArrow />
              </ArrowIconContainer>
            ) : null}

            {showShareFeedbackButton ? (
              <>
                <StyledButton style={{ marginLeft: 'auto' }} onClick={handleShareNewFeedbackClick}>
                  <VerticallyAlignedFlexContainer>
                    <StyledIconButton color="primary" borderColor="primary" size="tinier" padding="xLarge">
                      <FeedbackIcon size="fixed20px" color="primary" />
                    </StyledIconButton>
                    <Typography13 leftGutter={1} color="primary">
                      Share Feedback
                    </Typography13>
                  </VerticallyAlignedFlexContainer>
                </StyledButton>

                <ArrowIconContainer $isShareBtn>
                  <CurvedArrow />
                </ArrowIconContainer>
              </>
            ) : null}
          </VerticallyAlignedFlexContainer>
        </StyledBox>
        <StyledGrid container justify="center" alignItems="center" direction="column" topGutter={6}>
          <StyledImageContainer>
            <StyledImg alt="no-feedback-image" src={NoFeedback} />
          </StyledImageContainer>
          <TextBodyMedium bold mt={3}>
            Nothing to see here yet.
          </TextBodyMedium>
          <TextBodyMedium textAlign="center" mt={1} mb={4} color="text.g2">
            Nothing to see here yet. Request feedback for better performance enablement!
          </TextBodyMedium>
        </StyledGrid>
      </>
    );
  }

  return (
    <div>
      <Feedbacks
        headerText="Praise"
        type={SENTIMENTS_TYPES.appreciative.value}
        hasCount={false}
        count={0}
        noHorizontalPadding
        fetchDataFn={fetchPraise}
        employeeId={employeeId}
        setIsPraiseEmpty={setIsPraiseEmpty}
        accessible={accessible}
        isCompetencySidePanel
        competencyId={competencyId}
      />
      <Divider1 topGutter={2} bottomGutter={2} />
      <Feedbacks
        headerText="Feedback"
        type={SENTIMENTS_TYPES.constructive.value}
        hasCount={false}
        count={0}
        noHorizontalPadding
        fetchDataFn={fetchFeedback}
        employeeId={employeeId}
        setIsAdviceEmpty={setIsAdviceEmpty}
        accessible={accessible}
        isCompetencySidePanel
        competencyId={competencyId}
      />
      <Divider1 topGutter={2} bottomGutter={2} />
    </div>
  );
};

const StyledImageContainer = styled.div`
  width: 33%;
`;

const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  aspect-ratio: 3 / 2;
`;

const ArrowIconContainer = styled.div`
  svg {
    fill: ${(props) => props.theme.palette.borders.lightGrey};
    width: 100%;
    height: auto;
  }
  position: absolute;
  top: 50px;
  right: ${(props) => (props.$isShareBtn ? '100px' : '110px')};
  width: 50px;
  transform: scaleY(-1) rotate(125deg);
  ${(props) => props.theme.breakpoints.down('xs')} {
    display: none;
  }
`;

export default PraiseAdviceSection;
